import * as React from 'preact/compat'

import { loadStylesheet, showModal } from '#js/common/helpers'
import { SGPlugin } from '#js/common/SGPlugins'

import Modal from '#components/Modal'

import styles from '#css/sg-alert.pcss.json'
import buttonsCss from '#css/v9/buttons.pcss.json'

declare global {
  interface Window {
    sgAlert: (message: string, okText?: string) => void,
    sgConfirm: (message: string, okText?: string) => Promise<boolean>
  }
}

interface IAlertProps {
  type: 'alert' | 'confirm',
  message: string,
  onClose: () => void,
  onConfirm?: () => void | 'halt',
  onCancel?: () => void,
  confirmText?: string,
  cancelText?: string,
  additionalActions?: Array<{
    label: string,
    action: () => void
  }>
}

export const Alert: React.FunctionComponent<IAlertProps> = ({
  type,
  message,
  onClose,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  additionalActions
}) => {
  // noinspection JSIgnoredPromiseFromCall
  loadStylesheet('sg-alert')
  // noinspection JSIgnoredPromiseFromCall
  loadStylesheet('v9/buttons')

  const modal = React.useRef<Modal>()
  const [alreadyTriggered, setAlreadyTriggered] = React.useState(false)

  const okClick = () => {
    setAlreadyTriggered(true)
    if (typeof onConfirm === 'function') {
      if (onConfirm() === 'halt') {
        return
      }
    }
    // noinspection JSIgnoredPromiseFromCall
    modal.current.close()
  }

  const cancelClick = () => {
    setAlreadyTriggered(true)
    if (typeof onCancel === 'function') {
      onCancel()
    }
    // noinspection JSIgnoredPromiseFromCall
    modal.current.close()
  }

  const overlayClick = () => {
    if (type !== 'confirm') {
      if (typeof onClose === 'function') {
        onClose()
      }
      return
    }
    if (typeof onCancel === 'function' && !alreadyTriggered) {
      onCancel()
    }
    if (typeof onClose === 'function') {
      onClose()
    }
  }

  let okText = confirmText || 'OK'

  const buttons = []

  if (type === 'confirm') {
    buttons.push(<button
      type='button'
      onClick={cancelClick}
      className={[buttonsCss['button'], buttonsCss['secondary']].join(' ')}
      disabled={alreadyTriggered}
    >{cancelText || 'Отмена'}</button>)
  }

  if (additionalActions) {
    additionalActions.forEach(action => {
      const callback = () => {
        action.action()
        cancelClick()
      }
      buttons.push(<button
        type='button'
        onClick={callback}
        className={[buttonsCss['button'], buttonsCss['secondary']].join(' ')}
        disabled={alreadyTriggered}
      >{action.label}</button>)
    })
  }

  buttons.push(<button
    type='button'
    onClick={okClick}
    className={[buttonsCss['button'], buttonsCss['green']].join(' ')}
    disabled={alreadyTriggered}
  >{okText}</button>)

  return <Modal ref={modal} onClose={overlayClick}>
    <p className={styles['message']} dangerouslySetInnerHTML={{ __html: message }}/>
    <div className={styles['buttons']}>
      {buttons}
    </div>
  </Modal>
}

const showAlert = (type: 'alert' | 'confirm', message: string, onConfirm?: () => void, onCancel?: () => void, confirmText?: string, additionalProps?: Partial<IAlertProps>) => {
  showModal(Alert, {
    type,
    onConfirm,
    onCancel,
    message,
    confirmText,
    ...additionalProps
  })
}

export const sgAlert = (message: string, okText?: string, additionalProps?: Partial<IAlertProps>) => {
  showAlert('alert', message, undefined, undefined, okText, additionalProps)
}

export const sgConfirm = (message: string, okText?: string, additionalProps?: Partial<IAlertProps>): Promise<boolean> => new Promise(resolve => {
  const onConfirm = () => resolve(true)
  const onCancel = () => resolve(false)
  showAlert('confirm', message, onConfirm, onCancel, okText, additionalProps)
})

if (typeof window !== 'undefined') {
  window.sgAlert = sgAlert
  window.sgConfirm = sgConfirm
}


// noinspection JSUnusedGlobalSymbols
export default (elements: NodeListOf<HTMLElement>) => {
  elements.forEach(element => {
    const alert = element.getAttribute('data-sg-alert')
    const confirm = element.getAttribute('data-sg-confirm')
    const confirmCallback = element.getAttribute('data-sg-confirm-callback')

    let type: 'alert' | 'confirm';
    let message = '';

    if (alert) {
      type = 'alert'
      message = alert
    } else if (confirm) {
      type = 'confirm'
      message = confirm
    }

    if (!type) {
      return
    }

    const onClick = (event: React.TargetedEvent) => {
      event.preventDefault()

      const onConfirm = (): void | 'halt' => {
        if (typeof window !== 'undefined') {
          if (!confirmCallback) {
            window.location.href = element.getAttribute('href')
            return 'halt'
          }
          if (typeof window[confirmCallback] === 'function') {
            window[confirmCallback](element)
            return
          }
        }
      }

      const prefix = `sg-${type}`
      const options = SGPlugin.getElementOptions(element, prefix, false)

      let okText = options['ok-text'] as string | undefined

      if (typeof okText !== 'string') {
        okText = undefined
      }

      showAlert(type, message, onConfirm, () => {}, okText)
    }
    element.addEventListener('click', onClick)
  })
}
